import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ProjectService } from 'src/app/private/services/project.service';
import { UsersService } from 'src/app/private/services/users.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-project-detail-members',
  templateUrl: './project-detail-members.component.html',
  styleUrls: ['./project-detail-members.component.scss']
})
export class ProjectDetailMembersComponent implements OnInit, OnChanges {
  @Input() projectId!: string;
  @Input() clientId!: string;
  @Input() projectMembers: any[] = [];
  @Input() currentUserId!: any;
  @Input() projectManagerId!: any;
  @Output() membersChanged = new EventEmitter<void>();

  allClientUsers: any[] = [];
  availableUsers: any[] = [];
  selectedUserIdToAdd: any = null;
  loadingUsers = false;
  btnLoading = false;

  constructor(
    private projectService: ProjectService,
    private userService: UsersService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    console.log('ProjectMembers Component OnInit:', this.projectMembers);
    this.loadClientUsers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectMembers']) {
      console.log('ProjectMembers Component Input Changed:', this.projectMembers);
    }
    if (changes['projectMembers'] || changes['clientId']) {
      this.filterAvailableUsers();
    }
  }

  loadClientUsers(): void {
    if (!this.clientId) return;
    this.loadingUsers = true;
    this.userService.findAllUsers({ client_id: this.clientId }).subscribe({
      next: (res: any) => {
        this.allClientUsers = res?.data || res?.rows || res || [];
        this.filterAvailableUsers();
        this.loadingUsers = false;
      },
      error: () => {
        this.loadingUsers = false;
        this.toastr.error('Failed to load company users');
      }
    });
  }

  filterAvailableUsers(): void {
    const assignedIds = new Set((this.projectMembers || []).map(m => Number(m.user_id)));
    this.availableUsers = (this.allClientUsers || []).filter(u => !assignedIds.has(Number(u.user_id)));
  }

  onAddMemberClick(): void {
    if (!this.selectedUserIdToAdd) {
      this.toastr.warning('Please select a user to add');
      return;
    }
    this.btnLoading = true;
    const currentMemberIds = (this.projectMembers || []).map(m => Number(m.user_id));
    const newMemberIds = [...currentMemberIds, Number(this.selectedUserIdToAdd)];

    this.projectService.updateProject({
      project_id: this.projectId,
      client_id: this.clientId,
      member_user_ids: newMemberIds,
      where: { project_id: this.projectId }
    }).subscribe({
      next: () => {
        this.toastr.success('Member added successfully');
        this.selectedUserIdToAdd = null;
        this.btnLoading = false;
        this.membersChanged.emit();
      },
      error: (err) => {
        this.btnLoading = false;
        this.toastr.error(err?.error?.message || 'Failed to add member');
      }
    });
  }

  removeMember(userId: any): void {
    if (Number(userId) === Number(this.projectManagerId)) {
      this.toastr.error('Cannot remove the Project Manager. Change the Project Manager in the Settings tab first.');
      return;
    }
    if (!confirm('Are you sure you want to remove this member from the project?')) {
      return;
    }

    const currentMemberIds = (this.projectMembers || []).map(m => Number(m.user_id));
    const newMemberIds = currentMemberIds.filter(id => id !== Number(userId));

    this.projectService.updateProject({
      project_id: this.projectId,
      client_id: this.clientId,
      member_user_ids: newMemberIds,
      where: { project_id: this.projectId }
    }).subscribe({
      next: () => {
        this.toastr.success('Member removed successfully');
        this.membersChanged.emit();
      },
      error: (err) => {
        this.toastr.error(err?.error?.message || 'Failed to remove member');
      }
    });
  }

  isProjectManager(userId: any): boolean {
    if (!userId || !this.projectManagerId) return false;
    return Number(userId) === Number(this.projectManagerId);
  }

  getUserFullName(user: any): string {
    return `${user.prefix || ''} ${user.fname || ''} ${user.lname || ''}`.replace(/\s+/g, ' ').trim() || 'User';
  }

  getAvatarColor(m: any): string {
    const colors = ['#6366f1', '#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#ef4444'];
    const idx = (m.user_id || 0) % colors.length;
    return colors[idx];
  }

  getInitials(m: any): string {
    const fn = m.fname ? m.fname[0] : '';
    const ln = m.lname ? m.lname[0] : '';
    return (fn + ln).toUpperCase() || 'U';
  }
}
